import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { getRankingFull, getRankingLog, validarPartidas, calcularGrau, confirmarGrau } from '../../services/ranking.service';
import { RANKING_ESTADO } from '../../constants/ranking-estado';

// ── Estilos de celda compartidos ────────────────────────────────────────────
const headerCellSx = {
  color: '#60a5fa',
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  whiteSpace: 'nowrap',
  py: 1,
  px: 1.5,
  bgcolor: '#0d1525',
};

const bodyCellSx = {
  color: '#cbd5e1',
  fontSize: '0.82rem',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  py: 0.8,
  px: 1.5,
  whiteSpace: 'nowrap',
};

const nullCell = (val, fallback = '—') =>
  val != null && val !== '' ? val : fallback;

// ── Tabla genérica con scroll y ordenamiento client-side ────────────────────
function DataTable({ columns, rows, emptyMsg = 'Sin datos' }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIdx, setSelectedIdx] = useState(null);

  // Una columna es ordenable si tiene sortValue o su key no empieza con '_'
  const isSortable = (col) => !!(col.sortValue || !col.key.startsWith('_'));

  const getSortValue = (col, row) => {
    if (col.sortValue) return col.sortValue(row);
    const val = row[col.key];
    return val ?? '';
  };

  const handleSort = (col) => {
    if (!isSortable(col)) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
    setSelectedIdx(null); // limpiar selección al reordenar
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col || !isSortable(col)) return rows;
    return [...rows].sort((a, b) => {
      const va = getSortValue(col, a);
      const vb = getSortValue(col, b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir]);

  return (
    <TableContainer
      sx={{
        maxHeight: 420,
        border: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(15, 23, 42, 0.5)',
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((col) => {
              const sortable = isSortable(col);
              const isActive = sortKey === col.key;
              return (
                <TableCell
                  key={col.key}
                  onClick={() => handleSort(col)}
                  sx={{
                    ...headerCellSx,
                    cursor: sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    '&:hover': sortable ? { color: '#93c5fd' } : {},
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {col.label}
                    {sortable && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.65rem',
                          lineHeight: 1,
                          color: isActive ? '#60a5fa' : '#334155',
                          transition: 'color 0.15s',
                        }}
                      >
                        {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ ...bodyCellSx, textAlign: 'center', color: '#475569', py: 3 }}>
                {emptyMsg}
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row, i) => {
              const isSelected = selectedIdx === i;
              return (
                <TableRow
                  key={i}
                  onClick={() => setSelectedIdx(isSelected ? null : i)}
                  sx={{
                    cursor: 'pointer',
                    background: isSelected ? 'rgba(58,110,182,0.22)' : 'transparent',
                    '&:hover': {
                      background: isSelected
                        ? 'rgba(58,110,182,0.28)'
                        : 'rgba(58,110,182,0.07)',
                    },
                    transition: 'background 0.12s',
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} sx={bodyCellSx}>
                      {col.render ? col.render(row) : nullCell(row[col.key])}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Tab 0: Cálculo ────────────────────────────────────────────────────
function TabCalculo({ rkgEstado, operando, logs, onValidar, onCalcular, onConfirmar, readOnly = false }) {
  const confirmado = rkgEstado === RANKING_ESTADO.GRAU_CONFIRMADO;
  const btnSx = (active) => ({
    borderColor: active ? '#3a6eb6' : '#1e293b',
    color: active ? '#60a5fa' : '#334155',
    '&:hover': active ? { borderColor: '#2563eb', color: '#93c5fd' } : {},
    '&.Mui-disabled': { borderColor: '#1e293b', color: '#334155' },
    minWidth: 160,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        multiline
        minRows={10}
        maxRows={16}
        value={logs}
        placeholder="Sin actividad. Usá los botones para iniciar el procesamiento."
        InputProps={{ readOnly: true }}
        sx={{
          '& .MuiInputBase-root': {
            color: '#94a3b8',
            fontFamily: 'monospace',
            fontSize: '0.82rem',
            background: 'rgba(15, 23, 42, 0.6)',
            border: '1px solid rgba(255,255,255,0.08)',
          },
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& textarea::placeholder': { color: '#475569', opacity: 1 },
        }}
      />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Validar partidas */}
        <Button
          id="btn-validar-partidas"
          variant="outlined"
          startIcon={operando ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <AssignmentTurnedInIcon />}
          disabled={readOnly || rkgEstado !== RANKING_ESTADO.CREADO || operando}
          onClick={onValidar}
          sx={btnSx(!readOnly && rkgEstado === RANKING_ESTADO.CREADO && !operando)}
        >
          Validar partidas
        </Button>

        {/* Calcular GRAU */}
        <Button
          id="btn-calcular-grau"
          variant="outlined"
          startIcon={operando ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <PlayArrowIcon />}
          disabled={readOnly || rkgEstado !== RANKING_ESTADO.PARTIDAS_VALIDADAS || operando}
          onClick={onCalcular}
          sx={btnSx(!readOnly && rkgEstado === RANKING_ESTADO.PARTIDAS_VALIDADAS && !operando)}
        >
          Calcular GRAU
        </Button>

        {/* Confirmar GRAU */}
        <Button
          id="btn-confirmar-grau"
          variant="outlined"
          startIcon={operando ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <CheckCircleOutlineIcon />}
          disabled={readOnly || rkgEstado !== RANKING_ESTADO.GRAU_CALCULADO || operando}
          onClick={onConfirmar}
          sx={{
            ...btnSx(!readOnly && rkgEstado === RANKING_ESTADO.GRAU_CALCULADO && !operando),
            ...(confirmado ? { borderColor: '#10b981', color: '#10b981' } : {}),
          }}
        >
          {confirmado ? 'Confirmado ✓' : 'Confirmar GRAU'}
        </Button>
      </Box>
      {confirmado && (
        <Alert severity="success" sx={{ borderRadius: 0, py: 0.5 }}>
          Cálculo confirmado. {readOnly ? 'Avanzá al siguiente paso para ver los reportes.' : 'Ya podés avanzar al siguiente paso.'}
        </Alert>
      )}
    </Box>
  );
}

// ── Tab 1: Partidas ──────────────────────────────────────────────────────────
const PARTIDAS_COLS = [
  { key: 'torId', label: 'Torneo ID' },
  { key: 'ronId', label: 'Ronda' },
  { key: 'jugBlancas', label: 'Jug. Blancas' },
  { key: 'jugNegras', label: 'Jug. Negras' },
  { key: 'resultadoBlancas', label: 'Res. Blancas' },
  { key: 'resultadoNegras', label: 'Res. Negras' },
  {
    key: 'tipoResultado', label: 'Tipo Resultado',
    render: (r) => {
      switch (r.tipoResBlancas) {
        case 1:
          return 'Normal';
        case 2:
          return 'Ausencia';
        case 3:
          return 'Bye';
      }
    }
  },
  {
    key: 'computableGrau', label: 'Comp. GRAU',
    render: (r) => r.computableGrau === 1 ? 'S' : r.computableGrau === 0 ? 'N' : '—'
  },
  { key: 'compGrauMotivo', label: 'Motivo' },
  { key: 'tipoBye', label: 'Tipo Bye' },
];

// ── Tab 2: Jugadores x Torneo ────────────────────────────────────────────────
const JUGADORES_COLS = [
  { key: 'torId', label: 'Torneo ID' },
  { key: 'jugId', label: 'Jug ID' },
  {
    key: '_nombre', label: 'Jugador',
    render: (r) => `${r.apellido}, ${r.nombre}`,
    sortValue: (r) => `${r.apellido}, ${r.nombre}`,
  },
  { key: 'activo', label: 'Activo' },
  { key: 'partidasGrau', label: 'Partidas. Grau' },
  { key: 'partComputables', label: 'Partidas Computables' },
  { key: 'puntosObtenidos', label: 'Pts. Obtenidos' },
  { key: 'puntosEsperados', label: 'Pts. Esperados' },
  { key: 'performance', label: 'Performance' },
  { key: 'grauInicial', label: 'GRAU Inicial' },
  { key: 'categoriaInicial', label: 'Cat. Inicial' },
  { key: 'cambioGrau', label: 'Cambio GRAU' },
  { key: 'coeficienteK', label: 'Coef. K' },
  { key: 'coeficienteM', label: 'Coef. M' },
];

// ── Tab 3: Historial / Resultado por jugador ─────────────────────────────────
const HISTORIAL_COLS = [
  {
    key: '_nombre', label: 'Jugador',
    render: (r) => `${r.apellido}, ${r.nombre}`,
    sortValue: (r) => `${r.apellido}, ${r.nombre}`,
  },
  { key: 'puntaje', label: 'Puntaje' },
  {
    key: 'variacion', label: 'Variación',
    render: (r) => {
      const v = r.variacion;
      if (v == null || v === '') return '—';
      return v > 0 ? `+${v}` : String(v);
    },
  },
  { key: 'categoria', label: 'Categoría' },
  { key: 'categoriaAnterior', label: 'Cat. Anterior' },
];

// ── Tab 4: GrauLog ────────────────────────────────────────────────────────────
const LOG_COLS = [
  { key: 'glog_id', label: 'ID' },
  { key: 'glog_proceso', label: 'Proceso' },
  { key: 'glog_detalle', label: 'Detalle' },
];

function TabGrauLog({ rkgId }) {
  const [logRows, setLogRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const handleCargar = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getRankingLog(rkgId);
      setLogRows(data ?? []);
      setLoaded(true);
    } catch (err) {
      setError(`Error al cargar el log: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          id="btn-cargar-log"
          variant="outlined"
          onClick={handleCargar}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
          sx={{
            borderColor: '#334155',
            color: '#94a3b8',
            '&:hover': { borderColor: '#60a5fa', color: '#60a5fa' },
            '&.Mui-disabled': { borderColor: '#1e293b', color: '#334155' },
          }}
        >
          {loading ? 'Cargando…' : 'Cargar log'}
        </Button>
        {loaded && !loading && (
          <Typography variant="caption" sx={{ color: '#475569' }}>
            {logRows.length} registro{logRows.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ borderRadius: 0, py: 0.5 }}>{error}</Alert>}
      {loaded && (
        <DataTable
          columns={LOG_COLS}
          rows={logRows}
          emptyMsg="Sin registros de log para este ranking."
        />
      )}
    </Box>
  );
}

// ── Tab label badge ──────────────────────────────────────────────────────────
function tabLabel(label, count) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
      {label}
      {count != null && (
        <Box
          component="span"
          sx={{
            fontSize: '0.68rem',
            fontWeight: 700,
            bgcolor: 'rgba(58,110,182,0.25)',
            color: '#60a5fa',
            borderRadius: '4px',
            px: 0.7,
            py: 0.1,
            lineHeight: 1.6,
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function StepCalculo({ rkgId, onBack, onNext, readOnly = false }) {
  const [activeTab, setActiveTab] = useState(0);
  const [ranking, setRanking] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [logs, setLogs] = useState('');
  const [operando, setOperando] = useState(false);

  // calculoConfirmado se deriva del estado del ranking, no de estado local
  const rkgEstado = ranking?.estado ?? -1;
  const calculoConfirmado = rkgEstado === RANKING_ESTADO.GRAU_CONFIRMADO;

  const appendLog = (msg) => setLogs((prev) => (prev ? `${prev}\n${msg}` : msg));

  const recargarRanking = async () => {
    const data = await getRankingFull(rkgId);
    setRanking(data);
  };

  useEffect(() => {
    if (!rkgId) {
      setLoadError('No se encontró el ID del ranking. Volvé al paso anterior.');
      setLoadingData(false);
      return;
    }
    const load = async () => {
      setLoadingData(true);
      setLoadError('');
      try {
        const data = await getRankingFull(rkgId);
        setRanking(data);
      } catch (err) {
        setLoadError(`Error cargando datos del ranking: ${err?.message || err}`);
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, [rkgId]);

  const handleValidar = async () => {
    setOperando(true);
    appendLog('Validando partidas...');
    try {
      await validarPartidas(rkgId);
      appendLog('Partidas validadas.');
      await recargarRanking();
    } catch (err) {
      appendLog(`Error: ${err?.message || err}`);
    } finally {
      setOperando(false);
    }
  };

  const handleCalcular = async () => {
    setOperando(true);
    appendLog('Calculando grau...');
    try {
      await calcularGrau(rkgId);
      appendLog('Cálculo finalizado.');
      await recargarRanking();
    } catch (err) {
      appendLog(`Error: ${err?.message || err}`);
    } finally {
      setOperando(false);
    }
  };

  const handleConfirmar = async () => {
    setOperando(true);
    appendLog('Confirmando grau...');
    try {
      const result = await confirmarGrau(rkgId);
      appendLog('Confirmación finalizada.');
      appendLog('Actualizando jugadores activos...');
      if (result?.advertencia) {
        appendLog(`Advertencia: ${result.advertencia}`);
      } else {
        appendLog('Jugadores activos actualizados.');
      }
      await recargarRanking();
    } catch (err) {
      appendLog(`Error: ${err?.message || err}`);
    } finally {
      setOperando(false);
    }
  };

  const partidas = ranking?.partidas ?? [];
  const jugadores = ranking?.jugadoresRanking ?? [];
  const historial = ranking?.historialRanking ?? [];

  return (
    <Box>
      {/* Cabecera con fecha e ID */}
      {ranking && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Ranking{' '}
            <span style={{ color: '#94a3b8', fontWeight: 600 }}>#{ranking.id}</span>
            {ranking.fecha && (
              <>
                {' '}— Fecha:{' '}
                <span style={{ color: '#94a3b8', fontWeight: 600 }}>{ranking.fecha}</span>
              </>
            )}
          </Typography>
        </Box>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          mb: 2,
          '& .MuiTab-root': { color: '#64748b', fontSize: '0.82rem', minHeight: 40, py: 0.5 },
          '& .Mui-selected': { color: '#60a5fa !important' },
          '& .MuiTabs-indicator': { bgcolor: '#3a6eb6' },
        }}
      >
        <Tab label="Cálculo" id="tab-log" aria-controls="tabpanel-log" />
        <Tab label={tabLabel('Partidas', loadingData ? null : partidas.length)}
          id="tab-partidas" aria-controls="tabpanel-partidas" />
        <Tab label={tabLabel('Jugadores x Torneo', loadingData ? null : jugadores.length)}
          id="tab-jugadores" aria-controls="tabpanel-jugadores" />
        <Tab label={tabLabel('Resultado por jugador', loadingData ? null : historial.length)}
          id="tab-historial" aria-controls="tabpanel-historial" />
        <Tab label="Log" id="tab-graulog" aria-controls="tabpanel-graulog" />
      </Tabs>

      {/* Contenido de tabs */}
      <Box sx={{ minHeight: 320 }}>
        {/* Spinner / Error global */}
        {loadingData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, gap: 2 }}>
            <CircularProgress size={28} sx={{ color: '#3a6eb6' }} />
            <Typography variant="body2" sx={{ color: '#64748b' }}>Cargando datos del ranking…</Typography>
          </Box>
        )}
        {!loadingData && loadError && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>{loadError}</Alert>
        )}

        {!loadingData && !loadError && (
          <>
            {/* Tab 0 — Calculo */}
            <Box role="tabpanel" id="tabpanel-log" hidden={activeTab !== 0}>
              {activeTab === 0 && (
                <TabCalculo
                  readOnly={readOnly}
                  rkgEstado={rkgEstado}
                  operando={operando}
                  logs={logs}
                  onValidar={handleValidar}
                  onCalcular={handleCalcular}
                  onConfirmar={handleConfirmar}
                />
              )}
            </Box>

            {/* Tab 1 — Partidas */}
            <Box role="tabpanel" id="tabpanel-partidas" hidden={activeTab !== 1}>
              {activeTab === 1 && (
                <DataTable
                  columns={PARTIDAS_COLS}
                  rows={partidas}
                  emptyMsg="Sin partidas asignadas a este ranking."
                />
              )}
            </Box>

            {/* Tab 2 — Jugadores x Torneo */}
            <Box role="tabpanel" id="tabpanel-jugadores" hidden={activeTab !== 2}>
              {activeTab === 2 && (
                <DataTable
                  columns={JUGADORES_COLS}
                  rows={jugadores}
                  emptyMsg="Sin jugadores asignados a este ranking."
                />
              )}
            </Box>

            {/* Tab 3 — Resultado por jugador */}
            <Box role="tabpanel" id="tabpanel-historial" hidden={activeTab !== 3}>
              {activeTab === 3 && (
                <DataTable
                  columns={HISTORIAL_COLS}
                  rows={historial}
                  emptyMsg="Sin historial para este ranking."
                />
              )}
            </Box>

            {/* Tab 4 — Log */}
            <Box role="tabpanel" id="tabpanel-graulog" hidden={activeTab !== 4}>
              {activeTab === 4 && <TabGrauLog rkgId={rkgId} />}
            </Box>
          </>
        )}
      </Box>

      <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* Botones de navegación */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ borderColor: '#334155', color: '#94a3b8', '&:hover': { borderColor: '#94a3b8' } }}
        >
          Anterior
        </Button>
        <Button
          variant="contained"
          endIcon={<NavigateNextIcon />}
          onClick={onNext}
          disabled={!readOnly && !calculoConfirmado}
          sx={{
            bgcolor: '#3a6eb6',
            '&:hover': { bgcolor: '#2563eb' },
            '&.Mui-disabled': { bgcolor: '#1e293b', color: '#334155' },
          }}
        >
          Siguiente
        </Button>
      </Box>
    </Box>
  );
}
